import Backbone from 'backbone'
import template from './dataset-field-list.hbs'
import _ from 'lodash'
import TreeActions from 'scoreboard/components/tree-actions'
import i18n from 'scoreboard/components/i18n'
import MarkedField from 'scoreboard/components/marked-field'
import Dataset from 'scoreboard/datasets/model/dataset'
const { TOTAL, ALL_TIME } = Dataset
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'
import renderFieldDropdown, { addEditFields } from 'scoreboard/datasets/render-field-dropdown'
import dataTypes from 'scoreboard/datasets/edit/data-types'
import { Transform } from 'stream'
import NoObjectView from 'scoreboard/components/no-object'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATASET_LINK } = DataType

/**
 * Embedded within the dataset-field-selector to draw a list of fields
 */
const DatasetFieldList = Backbone.View.extend({

	className: 'dataset-field-list tree-container tree-container-light',

	/** @type {Dataset} */
	model: null,

	/**
	 * @param {Object} opt
	 *
	 * @param {Boolean} [opt.allowEditFields=false] whether to include the
	 * Edit pseudo-fields (i.e. Record is Editable, Record Edited)
	 */
	initialize: function (opt) {
		this.options = _.extend({ filter: _.constant(true) }, opt)
	},

	render: function () {
		this.$el.append(template())

		let fields = this.validFields()
		this.options.allowEditFields && addEditFields(fields)

		this.gardener = new DatasetFieldGardener({
			dataset: this.model,
			fields: new Backbone.Collection(fields),
			allowTotal: this.options.allowTotal,
			finderOptions: {
				type: 'list'
			},
			sort: this.options.sort
		})

		this.$('.gardener').replaceWith(this.gardener.el)
		this.gardener.render()
		this.gardener.show().startStream()

		this._setupTreeAction()

		this._renderDateField()

		this._renderNoFieldsView(fields)

		return this
	},

	validFields: function () {
		let fields = _.cloneDeep(this.options.allowVisibleLinkedFields ?
			this.model.combinedFields : this.model.get('fields'))
		fields.push(... _.map(this.model.get('visibleLinkedFields') || [], vlf => {
			let fieldIdentifier = -1 * vlf.id
			let field = this.model.getFieldByFieldIdentifier(fieldIdentifier)
			return _.defaults({
				id: fieldIdentifier,
				label: this.model.getFieldLabel(fieldIdentifier)
			}, field)
		}))
		fields = _.filter(fields, field => {
			let vlfId = Dataset.isVisibleLinkedFieldIdentifier(field.id) ? field.id : null

			field = this.model.getFieldByFieldIdentifier(field.id)
			if (!this.model.get('administer') && field.hideFromNonAdmins) {
				return false
			}

			return this.options.filter(field, this.model, vlfId)
		})
		return fields
	},

	/**
	 * Removes the field with the given ID form the list
	 */
	removeField: function (fieldId) {
		this.gardener.tree.removeNode(fieldId)
		this._renderNoFieldsView(this.validFields())
	},

	_renderNoFieldsView: function (fields) {
		this.removeNoFieldsView()

		let fieldCount = fields.length
		if (this.options.allowTotal) {
			// If allowTotal, then "Number of X" will be one of the "fields" available
			fieldCount++
		}

		let hasFields = fieldCount > 0

		// Hide the tree controls if there aren't any fields to select
		this.$('.tree').toggleClass('is-not-displayed', !hasFields)

		// Show a "no fields available" message if there aren't any fields to select
		if (!hasFields) {
			this.gardener.clearLoadingDots()
			this.noFieldsView = new NoObjectView({
				section: 'datasets',
				noHeader: true,
				escapeNoTypeText: false,
				noTypeText: this.options.noFieldsMessage
			})

			this.$el.append(this.noFieldsView.el)
			this.noFieldsView.render()
		}
	},

	_setupTreeAction: function() {
		this.treeActions && this.treeActions.remove()

		this.treeActions = new TreeActions({
			el: this.gardener.tree.el.node(),
			tree: this.gardener.tree,
			buttonContent: i18n.get('common.add'),
			nodesReselectable: true
		}).render()
		this.treeActions.on('nodeActivated', (node) => this.checkReadyToSelect(node))
			.on('treeActionReset', () => this.checkReadyToSelect())
			.on('selected', () => this.select())
			// If the user attempted to select a disabled node, it means the user hasn't selected a valid Date Field yet, so
			// run markedField.validate() to trigger the error tooltip
			.on('selectionDisabled', () => this.markedField && this.markedField.validate())
	},

	_renderDateField: function () {
		this.$el.toggleClass('has-date-field', !!this.options.allowDateField)

		if (!this.options.allowDateField) {
			return
		}

		var fields = []

		let selectedId = this.model.idsToFieldIdentifier({
			fieldId: this.model.get('defaultCalendarFieldId'),
			visibleLinkedFieldId: this.model.get('defaultCalendarVisibleLinkedFieldId')
		})
		if (_.isNil(selectedId) && this.options.allowAllTime) {
			selectedId = ALL_TIME
		}

		fields.push(...this.model.combinedFields.filter(f => f.dataType === DATE))

		let allowedVisibleLinkedFields = _.filter(this.model.get('visibleLinkedFields'), vlf => {
			let field = this.model.getFieldByIds({
				fieldId: vlf.fieldId,
				visibleLinkedFieldId: vlf.id
			})
			return field.dataType === DATE
		})

		let selectOptions = {
			// https://github.com/SpiderStrategies/Scoreboard/issues/14539#issuecomment-346399478
			placeholder: i18n.get('dashboards.datasets.dateField.placeholder')
			// #25343 don't include a render method here, or it messes up the
			// display of linked dataset names
		}

		renderFieldDropdown({
			el: this.$('select'),
			dataset: this.model,
			allowedFieldIds: _.map(fields, 'id'),
			allowedVisibleLinkedFieldIds: _.map(allowedVisibleLinkedFields, 'id'),
			includeAllTime: this.options.allowAllTime,
			selectedId,
			selectOptions
		})

		this.markedField = new MarkedField({
			el: this.$('select'),
			validator: dateFieldValidator
		}).render()

		this.$('select').on('change', () => this.checkReadyToSelect())
	},

	select: function () {
		var selected = this.gardener.tree.selected()
		let selectedFieldIds = this.model.fieldIdentifierToIds(selected.id)

		let calendarFieldIdentifier = this.options.allowDateField ? this.$('select').val() : null
		let calendarFieldIds = this.model.fieldIdentifierToIds(calendarFieldIdentifier)
		this.treeActions.applyCheckmark()

		this.trigger('select', {
			dataset: this.model,
			calendarFieldId: _.get(calendarFieldIds, 'fieldId'),
			calendarVisibleLinkedFieldId: _.get(calendarFieldIds, 'visibleLinkedFieldId'),
			fieldIdentifier: selected.id,
			fieldId: _.get(selectedFieldIds, 'fieldId'),
			visibleLinkedFieldId: _.get(selectedFieldIds, 'visibleLinkedFieldId'),
			field: this.model.getFieldByFieldIdentifier(selected.id)
		})
	},

	checkReadyToSelect: function (treeNode) {
		treeNode = treeNode || this.$('.node.selected')

		if (!treeNode) {
			return
		}

		var node = this.gardener.tree.get(treeNode.data('id'))

		var allowSelect = this.allowSelect(node)
		treeNode.find('.label-mask').toggleClass('is-disabled', !allowSelect)
	},

	allowSelect: function (node) {
		// If node doesn't have a checkmark, it can be added
		var nodeEnabled = !this.$(`.node[data-id="${node.id}"] .label-mask`).hasClass('is-checked') ? true : false

		if (this.options.allowDateField) {
			return !this.markedField.test() && nodeEnabled
		} else {
			return nodeEnabled
		}
	},

	removeNoFieldsView: function () {
		_.invoke(this.noFieldsView, 'remove')
		delete this.noFieldsView
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.treeActions, this.markedField, this.gardener], 'remove')
		this.removeNoFieldsView()
	}
})

function dateFieldValidator (val) {
	return !val ? i18n.get('dashboards.datasets.dateField.required') : null
}

import Gardener from 'scoreboard/components/gardener'
import Stream from 'stream'
import { isJunctionLinkField } from '../../forms/dataset-field-filter'

// A simple gardener implementation that uses a fake stream that just emits the fields passed in
const DatasetFieldGardener = Gardener.extend({
	initialize: function (opt) {
		this.fields = opt.fields
		this.allowTotal = opt.allowTotal
		this.dataset = opt.dataset
		this.sort = opt.sort
		Gardener.prototype.initialize.apply(this, opt)
	},

	_treeOptions: function () {
		let treeOpt = Gardener.prototype._treeOptions.apply(this, arguments)
		// so the -1 TOTAL option isn't treated as transient. But don't use
		// a value below -99 since visible linked field IDs start at -100
		treeOpt.transientId = -99
		treeOpt.label = selection => {
			if (selection.select('span').size()) {
				// See if we already drew the span. If so we don't need to reset the html.
				// It seems like resetting the html is fine, but for some reason it breaks the
				// tree actions event handling
				return
			}
			selection.html(d => {
				let label = `<span>${_.escape(this.tree.nodes[d.id].label)}</span>`
				let datasetForField = this.dataset.getDatasetForField(d.id)

				if (!_.isNil(datasetForField) && this.dataset.id != datasetForField.id) {
					label += ` <span class="linked-dataset-label">(${_.escape(datasetForField.get('label'))})</span>`
				}

				return label
			})
		}

		treeOpt.accessors = { icon: 'icon' }
		var map = new Transform({ objectMode: true })
		let dataset = this.dataset
		map._transform = function (node, enc, next) {
			let dataTypeInfo = _.find(dataTypes, dt => {
				let underlyingField = dataset.getUnderlyingField(node)
				return dt.id === underlyingField.dataType
			})
			// If no data type was matched that indicates it's a Total field (e.g. Number of Sexual Assaults),
			// so the correct dataType is number.
			let icon = dataTypeInfo ? dataTypeInfo.icon : 'number'
			if (node.dataType === DATASET_LINK || isJunctionLinkField(node, dataset)) {
				// #45892 dataset links should use a dedicated icon
				icon = DATASET_LINK
			}
			node.icon = icon
			this.push(node)
			next()
		}
		treeOpt.stream = treeOpt.stream.pipe(map)

		return treeOpt
	},

	_stream: function() {
		this._fakeStream = new Stream()
		this._fakeStream.pause = this._fakeStream.resume = _.identity
		return this._fakeStream
	},

	startStream: function() {
		let fieldData = this.sort ? this.sort(this.fields.toJSON()) :
			_.sortBy(this.fields.toJSON(), (f) => f.label.toLocaleUpperCase())

		if (this.allowTotal) {
			fieldData.splice(0, 0, {
				id: TOTAL,
				label: fieldAggregationDescription({
					dataset: this.dataset,
					tracked: this.dataset.get('tracked')
				})
			})
		}
		fieldData.forEach(field => this._fakeStream.emit('data', field))

		this._fakeStream.finished = true
		this._fakeStream.emit('end')

	}
})


export default DatasetFieldList

DatasetFieldList.__filename__ = 'components/bootslide/dataset-field-list.js'
